import React, { useEffect } from 'react'
import { PresetConfigLoader } from '../../../views/Setup/AIFeaturePresets/PresetConfig/Common/PresetConfigLoader'
import { useMeetingTemplateEffectiveInputParameters, useInputParametersUpdateScope, useUserInputParameterConfigForm } from '../hooks'
import { MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../model'
import { YesNoCancelModal } from '../../Modal/YesNoCancelModal'
import { InputParameterConfigForm } from '../InputParameterConfigForm'
import { Button } from '../../Button/Button'

export const MeetingTemplateGlobalInputParameterConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  type,
  typeId,
  inputParametersSchema,
  defaultInputParameters,
  meetingTemplateId,
  showCustomizationSetting,
  onHide,
}) => {
  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    type,
    typeId,
    defaultInputParameters,
    undefined,
    meetingTemplateId,
  )

  if (!inputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <MeetingTemplateGlobalInputParameterConfigFormBody
      inputParametersSchema={inputParametersSchema}
      inputParameters={inputParameters}
      parentInputParameters={parentInputParameters}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
    />
  )
}

interface MeetingTemplateGlobalInputParameterConfigFormBodyProps {
  inputParametersSchema: { [key: string]: any } | undefined
  inputParameters: { [key: string]: any }
  parentInputParameters: { [key: string]: any }
  isCustomized: boolean
  showCustomizationSetting: boolean
  onSave: (inputParameters: { [key: string]: any }, isCustomized: boolean) => Promise<void>
  onHide: () => void
  updateLoading: boolean
}

const MeetingTemplateGlobalInputParameterConfigFormBody: React.FC<MeetingTemplateGlobalInputParameterConfigFormBodyProps> = ({
  inputParametersSchema,
  parentInputParameters,
  inputParameters,
  isCustomized,
  showCustomizationSetting,
  onSave,
  onHide,
  updateLoading,
}) => {
  const { hasIpHierarchyAdminScope } = useInputParametersUpdateScope()

  const {
    editedInputParameters,
    customized,
    isChanged,
    showConfirmation,
    setEditedInputParameters,
    setShowConfirmation,
    handleChangeCustomized,
    handleClickCancel,
    handleClickSave,
    handleClickClose,
    checkForChange,
  } = useUserInputParameterConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide)

  useEffect(() => {
    checkForChange()
  }, [checkForChange])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters, customized)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <InputParameterConfigForm
        inputParametersSchema={inputParametersSchema}
        inputParameters={editedInputParameters}
        showCustomizationSetting={showCustomizationSetting}
        customized={customized}
        onChange={setEditedInputParameters}
        onChangeCustomized={handleChangeCustomized}
        disabled={!hasIpHierarchyAdminScope}
      />
      <div className='w-100 d-flex justify-content-between zocks-mt-3'>
        <Button variant='borderless' onClick={handleClickClose}>
          Close
        </Button>
        {hasIpHierarchyAdminScope && (
          <div className='d-flex gap-2'>
            <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading}>
              Save
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
